/**
 * Centralized API client for Royal Chess Kingdom.
 *
 * One axios instance. Two guarantees:
 *  1. `withCredentials: true` is always on — the HttpOnly auth_token cookie
 *     is sent on every request, even when the in-memory JWT is null (e.g.
 *     PWA cold-start before /auth/me restores it).
 *  2. If a JWT is in memory, it's auto-attached as `Authorization: Bearer …`.
 *     Components don't have to remember the cookie/Bearer dance.
 *
 * Wiring:
 *   - `AuthContext` calls `setApiToken(token)` whenever the token state
 *     changes (login, restoreSession, logout). The interceptor reads from
 *     this module-level variable on every request.
 *   - URLs are RELATIVE to `${REACT_APP_BACKEND_URL}/api` — components write
 *     `apiClient.get('/profile/stats')` instead of repeating `${API}` paths.
 *
 * The `sameOriginRewriter` already patches the underlying browser fetch /
 * XMLHttpRequest, so calls from the custom production domain still land
 * same-origin even though baseURL points at the env-configured backend.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  // Fail fast at boot — better than silently sending requests to "/api".
  // Caught by anyone running the bundle without the env var configured.
  // eslint-disable-next-line no-console
  console.error('[apiClient] REACT_APP_BACKEND_URL is not set. API calls will fail.');
}

let inMemoryToken = null;

export const setApiToken = (token) => {
  inMemoryToken = token || null;
};

export const getApiToken = () => inMemoryToken;

const apiClient = axios.create({
  baseURL: `${BACKEND_URL || ''}/api`,
  withCredentials: true,
  timeout: 30000,
});

apiClient.interceptors.request.use((config) => {
  if (inMemoryToken) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${inMemoryToken}`;
    }
  }
  return config;
});

export default apiClient;
